import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { formatUnitFieldValue } from "@/lib/config/unit-field-config";
import type { TerrainSpeed } from "@/lib/utils/terrain-speed-calculator";
import { formatTerrainName } from "@/lib/utils/terrain-speed-calculator";
import { cn } from "@/lib/utils";

interface SpeedFieldDisplayProps {
  speed: number;
  isModified?: boolean;
  baseValue?: number;
  terrainSpeeds?: TerrainSpeed[];
  className?: string;
  /** Label + value on one row; terrain table still expands below */
  compact?: boolean;
}

/**
 * Enhanced speed field display with expandable terrain speeds section
 */
export function SpeedFieldDisplay({
  speed,
  isModified = false,
  baseValue,
  terrainSpeeds = [],
  className = "",
  compact = false,
}: SpeedFieldDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasTerrainSpeeds = terrainSpeeds && terrainSpeeds.length > 0;

  const formattedSpeed = formatUnitFieldValue("speed", speed);
  const formattedBaseValue = baseValue !== undefined
    ? formatUnitFieldValue("speed", baseValue)
    : undefined;

  const valueBlock = (
    <span className={cn("text-sm font-semibold", isModified && "text-primary")}>
      {formattedSpeed}
      {isModified && formattedBaseValue && (
        <span className="text-xs text-muted-foreground ml-1">({formattedBaseValue})</span>
      )}
    </span>
  );

  const terrainToggle = hasTerrainSpeeds ? (
    <button
      type="button"
      onClick={() => setIsExpanded(!isExpanded)}
      className="shrink-0 p-0 hover:opacity-70 transition-opacity"
      aria-label={isExpanded ? "Collapse terrain speeds" : "Expand terrain speeds"}
    >
      <ChevronDown
        className={cn(
          "h-3 w-3 text-muted-foreground transition-transform duration-200",
          isExpanded && "rotate-180",
        )}
      />
    </button>
  ) : null;

  return (
    <div className={cn("flex flex-col gap-0", className)}>
      {compact ? (
        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-2">
          <span className="min-w-0 text-xs text-muted-foreground">Speed</span>
          <div className="flex shrink-0 items-baseline justify-end gap-1">
            <span className="min-w-0 text-end tabular-nums">{valueBlock}</span>
            {terrainToggle}
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Speed</span>
            {terrainToggle}
          </div>
          <div className="flex items-center gap-1">{valueBlock}</div>
        </>
      )}

      {/* Expandable terrain speeds section */}
      {hasTerrainSpeeds && (
        <div
          className={cn(
            "overflow-hidden transition-all duration-200",
            isExpanded ? "max-h-96 opacity-100 mt-2" : "max-h-0 opacity-0",
          )}
        >
          <div className="space-y-1 pt-1 border-t border-border/50">
            {terrainSpeeds.map((terrainSpeed, idx) => (
              <div
                key={idx}
                className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 text-xs"
              >
                <span className="text-muted-foreground truncate">
                  {formatTerrainName(terrainSpeed.terrainName)}:
                </span>
                <span
                  className={`shrink-0 text-end font-semibold tabular-nums ${
                    terrainSpeed.isModified ? "text-primary" : ""
                  }`}
                >
                  {terrainSpeed.speed} km/h
                  {terrainSpeed.isModified &&
                    terrainSpeed.baseSpeed !== undefined && (
                      <span className="text-xs text-muted-foreground ml-1">
                        ({terrainSpeed.baseSpeed} km/h)
                      </span>
                    )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

